import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
} from "chart.js";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import { Button, Chip, Autocomplete, AutocompleteItem, CircularProgress } from "@nextui-org/react";
import { useInsightsData, useInsightsRecommendations, useOrganisations } from "./useInsightsData";
import { lineChartBaseOptions, lineDatasetStyle } from "../../utils/chartTheme";

ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler
);

const PERIOD_OPTIONS = [
  { key: "7d", label: "7 derniers jours" },
  { key: "14d", label: "14 derniers jours" },
  { key: "30d", label: "30 derniers jours" },
];

const FilterPill = ({ active, on_click, children }) => (
  <button
    type="button"
    onClick={on_click}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
      active
        ? "bg-primary text-white border-primary"
        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
    }`}
  >
    {children}
  </button>
);

const FilterBar = ({
  period,
  set_period,
  team,
  set_team,
  teams,
  department,
  set_department,
  departments,
  direction,
  set_direction,
}) => {
  const directions = [
    { key: "all", label: "Tous" },
    { key: "incoming", label: "Entrants" },
    { key: "outgoing", label: "Sortants" },
  ];
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
          Période
        </span>
        {PERIOD_OPTIONS.map((opt) => (
          <FilterPill
            key={opt.key}
            active={period === opt.key}
            on_click={() => set_period(opt.key)}
          >
            {opt.label}
          </FilterPill>
        ))}
      </div>
      <div className="w-px h-6 bg-gray-200" />
      <div className="flex items-center gap-2 min-w-[220px]">
        <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold whitespace-nowrap">
          Équipe
        </span>
        <Autocomplete
          size="sm"
          variant="bordered"
          aria-label="Équipe"
          placeholder="Toutes les équipes"
          selectedKey={team === "all" ? null : team}
          onSelectionChange={(key) => set_team(key || "all")}
          className="min-w-[180px]"
          allowsCustomValue={false}
        >
          {teams.map((item) => (
            <AutocompleteItem key={item.id}>{item.name}</AutocompleteItem>
          ))}
        </Autocomplete>
      </div>
      <div className="w-px h-6 bg-gray-200" />
      <div className="flex items-center gap-2 min-w-[220px]">
        <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold whitespace-nowrap">
          Département
        </span>
        <Autocomplete
          size="sm"
          variant="bordered"
          aria-label="Département"
          placeholder="Tous les départements"
          selectedKey={department === "all" ? null : department}
          onSelectionChange={(key) => set_department(key || "all")}
          className="min-w-[180px]"
          allowsCustomValue={false}
        >
          {departments.map((item) => (
            <AutocompleteItem key={item.id}>{item.name}</AutocompleteItem>
          ))}
        </Autocomplete>
      </div>
      <div className="w-px h-6 bg-gray-200" />
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
          Sens
        </span>
        {directions.map((d) => (
          <FilterPill
            key={d.key}
            active={direction === d.key}
            on_click={() => set_direction(d.key)}
          >
            {d.label}
          </FilterPill>
        ))}
      </div>
    </div>
  );
};

const GRADE_COLOR = (grade) => (grade >= 7 ? "#05CD99" : grade >= 5 ? "#FBBC05" : "#EF4040");

const DeltaBadge = ({ delta, suffix = "", invert = false, digits = 1 }) => {
  if (delta === null || delta === undefined) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const is_good = invert ? delta < 0 : delta > 0;
  const color = delta === 0 ? "text-gray-500" : is_good ? "text-emerald-600" : "text-red-500";
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "—";
  return (
    <span className={`text-xs font-semibold tabular-nums ${color}`}>
      {arrow} {Math.abs(delta).toFixed(digits)}
      {suffix}
    </span>
  );
};

const GradeMixBar = ({ health }) => (
  <div className="w-full">
    <div className="flex h-2.5 rounded-full overflow-hidden border border-gray-100 bg-gray-100">
      <div
        className="bg-grade-high"
        style={{ width: `${health.green_ratio * 100}%` }}
      />
      <div
        className="bg-grade-medium"
        style={{ width: `${health.orange_ratio * 100}%` }}
      />
      <div
        className="bg-grade-low"
        style={{ width: `${health.red_ratio * 100}%` }}
      />
    </div>
    <div className="flex text-[10px] text-gray-500 mt-1.5 tabular-nums gap-3 flex-wrap">
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-grade-high" />
        Verts {(health.green_ratio * 100).toFixed(0)}%
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-grade-medium" />
        Oranges {(health.orange_ratio * 100).toFixed(0)}%
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-grade-low" />
        Rouges {(health.red_ratio * 100).toFixed(0)}%
      </span>
    </div>
  </div>
);

// Hero in the app's own vocabulary: average grade /10 + green/orange/red mix,
// with the real previous-period delta — no invented composite score.
const HealthHero = ({ health, call_count, period_label }) => {
  const has_grade = health.avg_grade !== null && health.avg_grade !== undefined;
  const verdict = !has_grade
    ? "Pas encore de notes"
    : health.avg_grade >= 8
    ? "Qualité excellente"
    : health.avg_grade >= 6.5
    ? "Bonne qualité"
    : health.avg_grade >= 5
    ? "Qualité mitigée"
    : "Qualité à risque";
  return (
    <div className="bg-gradient-to-br from-primary/5 via-white to-emerald-50/40 border border-gray-200 rounded-xl px-6 py-6 flex items-center gap-10">
      <div className="flex flex-col items-center justify-center">
        <span
          className="text-6xl font-bold tabular-nums leading-none"
          style={{ color: has_grade ? GRADE_COLOR(health.avg_grade) : "#9CA3AF" }}
        >
          {has_grade ? health.avg_grade.toFixed(1) : "—"}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-gray-400 mt-2">
          note moyenne / 10
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
          Qualité des appels · {period_label}
        </div>
        <div className="text-2xl font-semibold text-gray-900 mb-1">{verdict}</div>
        <div className="text-sm text-gray-500 mb-3">
          {call_count.toLocaleString("fr-FR")} appels ·{" "}
          {health.graded_count.toLocaleString("fr-FR")} notés
        </div>
        <GradeMixBar health={health} />
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          {health.grade_delta !== null && health.grade_delta !== undefined ? (
            <>
              <DeltaBadge delta={health.grade_delta} />
              <span className="text-xs text-gray-500">
                vs période précédente ({health.prev_avg_grade?.toFixed(1)}/10)
              </span>
            </>
          ) : (
            <span className="text-xs text-gray-400">Pas de période de comparaison</span>
          )}
          {health.low_volume && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100">
              Volume faible — chiffres à interpréter avec prudence
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// The missing chart: grade over time, on the app's 0-10 scale.
const QualityTrendChart = ({ series }) => {
  const points = series || [];
  const labels = points.map((p) =>
    new Date(p.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
  );
  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: "Note moyenne",
          data: points.map((p) => p.avg_grade),
          ...lineDatasetStyle("#4318FF"),
          spanGaps: true,
        },
      ],
    }),
    [points]
  );
  const options = lineChartBaseOptions({
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const point = points[ctx.dataIndex];
            return `${ctx.parsed.y}/10 · ${point?.graded ?? 0} appels notés`;
          },
        },
      },
    },
    scales: {
      y: { min: 0, max: 10, ticks: { stepSize: 2 } },
    },
  });
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Évolution de la qualité</h3>
      <p className="text-[11px] text-gray-500 mb-4">Note moyenne quotidienne / 10</p>
      <div style={{ height: 240 }}>
        <Line
          data={data}
          options={options}
        />
      </div>
    </div>
  );
};

// The "who" behind the weak axes: teams (or departments, when a team filter is
// active) that are declining or sitting at a low grade. Volume-gated backend-side.
const WatchSegmentsPanel = ({ segments }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex items-center gap-2 mb-1">
      <span className="w-2 h-2 rounded-full bg-amber-400" />
      <h3 className="text-sm font-semibold text-gray-900">Points d&apos;attention</h3>
    </div>
    <p className="text-[11px] text-gray-500 mb-4">
      Équipes en baisse ou sous la moyenne, à volume suffisant
    </p>
    {(!segments || segments.length === 0) && (
      <p className="text-xs text-gray-400 py-6 text-center">Rien à signaler sur la période 🎉</p>
    )}
    <div className="flex flex-col gap-3">
      {(segments || []).map((segment) => (
        <div
          key={`${segment.kind}-${segment.id}`}
          className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2.5"
        >
          <div className="min-w-0">
            <div className="text-sm text-gray-800 truncate">{segment.name}</div>
            <div className="text-[10px] text-gray-400">
              {segment.graded_count.toLocaleString("fr-FR")} appels notés
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span
              className="text-sm font-semibold tabular-nums"
              style={{ color: GRADE_COLOR(segment.avg_grade) }}
            >
              {segment.avg_grade.toFixed(1)}/10
            </span>
            <DeltaBadge delta={segment.delta} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const AxesPanel = ({ title, axes, tone }) => {
  const accent =
    tone === "weak"
      ? { dot: "bg-red-400", label: "text-red-600", bar: "bg-red-400/80" }
      : { dot: "bg-emerald-400", label: "text-emerald-600", bar: "bg-emerald-400/80" };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${accent.dot}`} />
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>
        <span className="text-[10px] text-gray-400">moyennes / 10</span>
      </div>
      {axes.length === 0 && (
        <p className="text-xs text-gray-400 py-4 text-center">
          Pas assez d&apos;appels évalués sur la période
        </p>
      )}
      <div className="flex flex-col gap-3">
        {axes.map((a) => {
          const pct = (a.avg_score / a.max_score) * 100;
          return (
            <div
              key={a.field_name}
              className="flex flex-col gap-1.5"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-800">{a.label}</span>
                <span className="flex items-center gap-2">
                  <DeltaBadge delta={a.delta} />
                  <span className={`text-sm font-semibold tabular-nums ${accent.label}`}>
                    {a.avg_score.toFixed(1)}
                  </span>
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full ${accent.bar}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="text-[10px] text-gray-400">
                Sur {a.sample_count.toLocaleString("fr-FR")} appels
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SentimentDonut = ({ distribution }) => {
  const total =
    (distribution.positive || 0) +
    (distribution.neutral || 0) +
    (distribution.negative || 0) +
    (distribution.unknown || 0);
  const data = useMemo(
    () => ({
      labels: ["Positif", "Neutre", "Négatif"],
      datasets: [
        {
          data: [distribution.positive, distribution.neutral, distribution.negative],
          backgroundColor: ["#05CD99", "#94A3B8", "#EF4040"],
          borderWidth: 0,
        },
      ],
    }),
    [distribution]
  );
  const options = {
    cutout: "70%",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.label}: ${ctx.parsed.toLocaleString("fr-FR")} (${(
              (ctx.parsed / total) *
              100
            ).toFixed(1)}%)`,
        },
      },
    },
    maintainAspectRatio: false,
  };
  const positive_pct = total > 0 ? Math.round((distribution.positive / total) * 100) : 0;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Sentiment client</h3>
      <p className="text-[11px] text-gray-500 mb-4">
        Répartition sur {total.toLocaleString("fr-FR")} appels
      </p>
      <div className="flex items-center gap-6">
        <div
          className="relative"
          style={{ width: 160, height: 160 }}
        >
          <Doughnut
            data={data}
            options={options}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-emerald-600 tabular-nums">
              {positive_pct}%
            </span>
            <span className="text-[10px] uppercase tracking-wide text-gray-400">positif</span>
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <SentimentLegendRow
            color="#05CD99"
            label="Positif"
            count={distribution.positive}
            total={total}
          />
          <SentimentLegendRow
            color="#94A3B8"
            label="Neutre"
            count={distribution.neutral}
            total={total}
          />
          <SentimentLegendRow
            color="#EF4040"
            label="Négatif"
            count={distribution.negative}
            total={total}
          />
        </div>
      </div>
    </div>
  );
};

const SentimentLegendRow = ({ color, label, count, total }) => {
  const pct = total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
  return (
    <div className="flex items-center gap-3">
      <span
        className="w-2.5 h-2.5 rounded-full"
        style={{ background: color }}
      />
      <span className="text-sm text-gray-700 flex-1">{label}</span>
      <span className="text-sm tabular-nums text-gray-900">{count.toLocaleString("fr-FR")}</span>
      <span className="text-xs text-gray-400 tabular-nums w-12 text-right">{pct}%</span>
    </div>
  );
};

const SentimentTwoLines = ({ series }) => {
  const labels = series.map((p) =>
    new Date(p.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
  );
  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: "% Positif",
          data: series.map((p) => p.positive),
          ...lineDatasetStyle("#05CD99", { fill: false }),
        },
        {
          label: "% Neutre",
          data: series.map((p) => p.neutral),
          ...lineDatasetStyle("#94A3B8", { fill: false }),
          borderDash: [4, 4],
        },
        {
          label: "% Négatif",
          data: series.map((p) => p.negative),
          ...lineDatasetStyle("#EF4040", { fill: false }),
        },
      ],
    }),
    [labels, series]
  );
  const options = lineChartBaseOptions({
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { callback: (v) => `${v}%` },
      },
    },
  });
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 col-span-1 lg:col-span-2">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Sentiment dans le temps</h3>
      <p className="text-[11px] text-gray-500 mb-4">
        Trois courbes · positif (vert), neutre (gris pointillé), négatif (rouge)
      </p>
      <div style={{ height: 240 }}>
        <Line
          data={data}
          options={options}
        />
      </div>
    </div>
  );
};

const FrustrationThemesList = ({ themes }) => {
  const max_count = Math.max(...themes.map((t) => t.count), 1);
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Motifs de frustration</h3>
      <p className="text-[11px] text-gray-500 mb-4">
        Top {themes.length} causes citées par les clients
      </p>
      <div className="flex flex-col gap-3">
        {themes.map((theme, idx) => {
          const pct = (theme.count / max_count) * 100;
          return (
            <div
              key={theme.key}
              className="flex flex-col gap-1.5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-gray-400 tabular-nums w-4">
                    #{idx + 1}
                  </span>
                  <span className="text-sm text-gray-800">{theme.label}</span>
                </div>
                <span className="flex items-center gap-2">
                  <DeltaBadge
                    delta={theme.delta}
                    invert
                    digits={0}
                  />
                  <span className="text-sm font-semibold text-gray-900 tabular-nums">
                    {theme.count}
                  </span>
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden ml-6">
                <div
                  className="h-full rounded-full bg-amber-400/80"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const THEME_SERIES_COLORS = ["#F59E0B", "#3B82F6", "#EF4040"];

const RecurringIssuesChart = ({ timeseries, resolve_theme_label }) => {
  const themes = timeseries?.themes || [];
  const points = timeseries?.points || [];
  const labels = points.map((p) =>
    new Date(p.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
  );
  const data = useMemo(
    () => ({
      labels,
      datasets: themes.map((theme, idx) => ({
        label: resolve_theme_label(theme),
        data: points.map((p) => p.counts?.[theme] || 0),
        ...lineDatasetStyle(THEME_SERIES_COLORS[idx % THEME_SERIES_COLORS.length]),
      })),
    }),
    [themes, points]
  );
  const options = lineChartBaseOptions({
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  });
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">
        Problèmes récurrents dans le temps
      </h3>
      <p className="text-[11px] text-gray-500 mb-4">
        Appels frustrés par jour, pour les principaux motifs de la période
      </p>
      {themes.length === 0 ? (
        <p className="text-xs text-gray-400 py-8 text-center">
          Aucun motif de frustration détecté sur la période
        </p>
      ) : (
        <div style={{ height: 260 }}>
          <Line
            data={data}
            options={options}
          />
        </div>
      )}
    </div>
  );
};

const PRIORITY_TONE = {
  high: { chip: "bg-red-50 text-red-700 border-red-100", label: "Priorité élevée" },
  medium: { chip: "bg-amber-50 text-amber-700 border-amber-100", label: "Priorité moyenne" },
  low: { chip: "bg-gray-100 text-gray-600 border-gray-200", label: "Priorité basse" },
};

const RecommendationsPanel = ({ doc, loading, generating, error, on_generate }) => {
  const generated = doc?.generated_at
    ? new Date(doc.generated_at).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Plan d&apos;action</h3>
          <p className="text-[11px] text-gray-500">
            {generated
              ? `Synthèse générée par IA · ${generated} · 30 derniers jours`
              : "Synthèse générée par IA sur les 30 derniers jours"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Chip
            size="sm"
            variant="flat"
            className="bg-gray-100 text-gray-600"
          >
            Vue globale entreprise
          </Chip>
          <Button
            size="sm"
            variant="bordered"
            className="border-gray-200 text-gray-700"
            isLoading={generating}
            onPress={on_generate}
          >
            {doc ? "Régénérer" : "Générer le plan d'action"}
          </Button>
        </div>
      </div>
      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
      {loading && !doc && (
        <div className="flex justify-center py-8">
          <CircularProgress
            size="sm"
            aria-label="Chargement"
            color="primary"
          />
        </div>
      )}
      {!loading && !doc && !generating && (
        <p className="text-sm text-gray-400 py-8 text-center">
          Aucun plan d&apos;action généré pour le moment.
        </p>
      )}
      {doc && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          {doc.recommendations.map((rec, idx) => {
            const tone = PRIORITY_TONE[rec.priority] || PRIORITY_TONE.medium;
            return (
              <div
                key={`rec-${idx}`}
                className="border border-gray-200 rounded-lg p-4 flex flex-col gap-3 bg-gray-50/40"
              >
                <div className="flex items-start justify-between gap-3">
                  <h4 className="text-sm font-semibold text-gray-900 leading-snug">{rec.title}</h4>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border whitespace-nowrap ${tone.chip}`}
                  >
                    {tone.label}
                  </span>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
                    Constat
                  </div>
                  <p className="text-xs text-gray-700 leading-relaxed">{rec.rationale}</p>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
                    Action recommandée
                  </div>
                  <p className="text-xs text-gray-700 leading-relaxed">{rec.action}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Insights = () => {
  const { t } = useTranslation();
  const [period, set_period] = useState("30d");
  const [team, set_team] = useState("all");
  const [department, set_department] = useState("all");
  const [direction, set_direction] = useState("all");

  const { teams, departments } = useOrganisations();
  const { data, loading, error, refetch } = useInsightsData({
    period,
    team,
    department,
    direction,
  });
  const {
    doc: recommendations_doc,
    loading: recommendations_loading,
    generating,
    error: recommendations_error,
    generate,
  } = useInsightsRecommendations();

  // Axis labels: company display_name from the backend, else the Sider
  // criterion translation, else the humanised key.
  const resolve_axis_label = (axis) => {
    if (axis.label) return axis.label;
    const translation_key = `analysis.gradeLabels.${axis.field_name}`;
    const translated = t(translation_key);
    if (translated !== translation_key) return translated;
    return axis.field_name.replace(/_/g, " ");
  };
  const resolve_theme_label = (theme_key) => {
    const translation_key = `analysis.frustrationThemes.${theme_key}`;
    const translated = t(translation_key);
    if (translated !== translation_key) return translated;
    return String(theme_key).replace(/_/g, " ");
  };

  const axes = data?.axes || [];
  const axes_weak = axes.slice(0, 5).map((a) => ({ ...a, label: resolve_axis_label(a) }));
  const axes_strong = axes
    .slice(-5)
    .reverse()
    .map((a) => ({ ...a, label: resolve_axis_label(a) }));
  const themes = (data?.frustration_themes_top || []).map((theme) => ({
    ...theme,
    label: resolve_theme_label(theme.key),
  }));
  const period_label = data ? `${data.period.days} derniers jours` : "";
  const has_calls = (data?.call_count || 0) > 0;

  return (
    <div className="p-6 max-w-[1400px] mx-auto flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("Insights")}</h1>
          <p className="text-sm text-gray-500">Vue d&apos;ensemble de la qualité de vos appels</p>
        </div>
        <div className="flex items-center gap-2">
          <Chip
            size="sm"
            variant="flat"
            className="bg-primary/10 text-primary"
          >
            Beta
          </Chip>
          {!loading && !error && data && (
            <Chip
              size="sm"
              variant="flat"
              className="bg-emerald-50 text-emerald-700 border border-emerald-100"
            >
              {data.call_count.toLocaleString("fr-FR")} appels analysés
            </Chip>
          )}
        </div>
      </div>

      <FilterBar
        period={period}
        set_period={set_period}
        team={team}
        set_team={set_team}
        teams={teams}
        department={department}
        set_department={set_department}
        departments={departments}
        direction={direction}
        set_direction={set_direction}
      />

      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 py-24 flex flex-col items-center justify-center gap-3">
          <CircularProgress
            aria-label="Chargement"
            color="primary"
          />
          <p className="text-sm text-gray-400">Agrégation des appels de la période…</p>
        </div>
      )}

      {!loading && error && (
        <div className="bg-white rounded-xl border border-red-200 py-16 flex flex-col items-center justify-center gap-3">
          <p className="text-sm text-red-600">Impossible de charger les insights: {error}</p>
          <Button
            size="sm"
            variant="bordered"
            className="border-gray-200 text-gray-700"
            onPress={refetch}
          >
            Réessayer
          </Button>
        </div>
      )}

      {!loading && !error && data && !has_calls && (
        <div className="bg-white rounded-xl border border-gray-200 py-24 flex flex-col items-center justify-center">
          <p className="text-sm text-gray-400">Aucun appel sur la période avec ces filtres.</p>
        </div>
      )}

      {!loading && !error && data && has_calls && (
        <>
          <HealthHero
            health={data.health}
            call_count={data.call_count}
            period_label={period_label}
          />

          <QualityTrendChart series={data.quality_timeseries} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <AxesPanel
              title="Axes faibles"
              axes={axes_weak}
              tone="weak"
            />
            <AxesPanel
              title="Axes forts"
              axes={axes_strong}
              tone="strong"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <WatchSegmentsPanel segments={data.watch_segments} />
            <FrustrationThemesList themes={themes} />
            <SentimentDonut distribution={data.sentiment_distribution} />
            <SentimentTwoLines series={data.sentiment_timeseries} />
          </div>

          <RecurringIssuesChart
            timeseries={data.recurring_themes_timeseries}
            resolve_theme_label={resolve_theme_label}
          />
        </>
      )}

      <RecommendationsPanel
        doc={recommendations_doc}
        loading={recommendations_loading}
        generating={generating}
        error={recommendations_error}
        on_generate={generate}
      />
    </div>
  );
};

export default Insights;
